import { AbilityId, ClientMessageType, Vec3 } from '@pvp/shared';
import { NetworkManager } from '../network/index.js';

interface MobileControls {
  lookPad: HTMLElement;
  movePad: HTMLElement;
  moveThumb: HTMLElement;
}

const TOUCH_TAP_SLOP = 12;
const LOOK_SENSITIVITY = 0.006;

export class InputManager {
  private keys: Set<string> = new Set();
  private abilityKeys: Map<string, AbilityId> = new Map();
  private onTargetClick: ((event: MouseEvent) => void) | null = null;
  private onTabTargetCycle: ((direction: number) => void) | null = null;
  private lastMovePayload = '';
  private lastSentRotation = Number.NaN;
  private mouseX = 0;
  private mouseY = 0;
  private isLeftMouseDown = false;
  private isRightMouseDown = false;
  private cameraYaw = 0;
  private mobileMoveInput: Vec3 = { x: 0, y: 0, z: 0 };
  private movePointerId: number | null = null;
  private lookPointerId: number | null = null;
  private lastLookX = 0;
  private lookStartX = 0;
  private lookStartY = 0;
  private lookMoved = false;
  private tapPointerId: number | null = null;
  private tapStartX = 0;
  private tapStartY = 0;
  private tapMoved = false;
  private controls: MobileControls | null = null;

  constructor(
    private net: NetworkManager,
    private canvas: HTMLCanvasElement,
  ) {
    window.addEventListener('keydown', (event) => this.onKeyDown(event));
    window.addEventListener('keyup', (event) => this.onKeyUp(event));
    canvas.addEventListener('mousedown', (event) => this.onMouseDown(event));
    canvas.addEventListener('mouseup', (event) => this.onMouseUp(event));
    canvas.addEventListener('mousemove', (event) => this.onMouseMove(event));
    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    canvas.addEventListener('pointerdown', (event) => this.onCanvasPointerDown(event));
    canvas.addEventListener('pointermove', (event) => this.onCanvasPointerMove(event));
    canvas.addEventListener('pointerup', (event) => this.onCanvasPointerUp(event));
    canvas.addEventListener('pointercancel', (event) => this.onCanvasPointerCancel(event));
  }

  bindMobileControls(controls: MobileControls): void {
    this.controls = controls;

    controls.movePad.addEventListener('pointerdown', (event) => this.onMovePadPointerDown(event));
    controls.movePad.addEventListener('pointermove', (event) => this.onMovePadPointerMove(event));
    controls.movePad.addEventListener('pointerup', (event) => this.onMovePadPointerUp(event));
    controls.movePad.addEventListener('pointercancel', (event) => this.onMovePadPointerUp(event));

    controls.lookPad.addEventListener('pointerdown', (event) => this.onLookPadPointerDown(event));
    controls.lookPad.addEventListener('pointermove', (event) => this.onLookPadPointerMove(event));
    controls.lookPad.addEventListener('pointerup', (event) => this.onLookPadPointerUp(event));
    controls.lookPad.addEventListener('pointercancel', (event) => this.onLookPadPointerUp(event));
  }

  setAbilityKeys(abilities: AbilityId[]): void {
    this.abilityKeys.clear();
    const codes = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6'];
    abilities.forEach((id, index) => {
      if (codes[index]) {
        this.abilityKeys.set(codes[index], id);
      }
    });
  }

  setTargetClickHandler(handler: (event: MouseEvent) => void): void {
    this.onTargetClick = handler;
  }

  setTabTargetHandler(handler: (direction: number) => void): void {
    this.onTabTargetCycle = handler;
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLInputElement) return;

    const key = event.key.toLowerCase();

    if (key === 'tab') {
      event.preventDefault();
      this.onTabTargetCycle?.(event.shiftKey ? -1 : 1);
      return;
    }

    this.keys.add(key);

    const abilityId = this.abilityKeys.get(event.code);
    if (abilityId) {
      this.net.send({ type: ClientMessageType.UseAbility, abilityId });
      return;
    }

    if (key === 'escape') {
      this.net.send({ type: ClientMessageType.CancelCast });
    }

    this.updateMovement();
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.keys.delete(event.key.toLowerCase());
    this.updateMovement();
  }

  private onMouseDown(event: MouseEvent): void {
    if (event.button === 0) {
      this.isLeftMouseDown = true;
      if (this.isRightMouseDown) {
        // Both buttons held: move forward instead of selecting target
        this.updateMovement();
        return;
      }
      this.onTargetClick?.(event);
      return;
    }

    if (event.button === 2) {
      this.isRightMouseDown = true;
      this.canvas.requestPointerLock();
      if (this.isLeftMouseDown) {
        this.updateMovement();
      }
    }
  }

  private onMouseUp(event: MouseEvent): void {
    if (event.button === 0) {
      this.isLeftMouseDown = false;
      this.updateMovement();
      return;
    }

    if (event.button === 2) {
      this.isRightMouseDown = false;
      document.exitPointerLock();
      this.updateMovement();
    }
  }

  private onMouseMove(event: MouseEvent): void {
    if (this.isRightMouseDown) {
      this.rotateCamera(event.movementX * 0.003);
    }

    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
  }

  private onCanvasPointerDown(event: PointerEvent): void {
    if (!this.isTouchPointer(event) || this.tapPointerId !== null) return;

    this.tapPointerId = event.pointerId;
    this.tapStartX = event.clientX;
    this.tapStartY = event.clientY;
    this.tapMoved = false;
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
    this.canvas.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  private onCanvasPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.tapPointerId) return;

    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
    if (Math.hypot(event.clientX - this.tapStartX, event.clientY - this.tapStartY) > TOUCH_TAP_SLOP) {
      this.tapMoved = true;
    }
  }

  private onCanvasPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.tapPointerId) return;

    const wasTap = !this.tapMoved;
    this.resetCanvasTap(event);
    if (wasTap && this.onTargetClick) {
      const synthetic = new MouseEvent('click', {
        clientX: event.clientX,
        clientY: event.clientY,
      });
      this.onTargetClick(synthetic);
    }
    event.preventDefault();
  }

  private onCanvasPointerCancel(event: PointerEvent): void {
    if (event.pointerId !== this.tapPointerId) return;
    this.resetCanvasTap(event);
  }

  private resetCanvasTap(event: PointerEvent): void {
    if (this.canvas.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }
    this.tapPointerId = null;
    this.tapMoved = false;
  }

  private onMovePadPointerDown(event: PointerEvent): void {
    if (!this.isTouchPointer(event) || !this.controls || this.movePointerId !== null) return;

    this.movePointerId = event.pointerId;
    this.controls.movePad.setPointerCapture(event.pointerId);
    this.updateMovePad(event);
    event.preventDefault();
  }

  private onMovePadPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.movePointerId) return;
    this.updateMovePad(event);
    event.preventDefault();
  }

  private onMovePadPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.movePointerId || !this.controls) return;

    if (this.controls.movePad.hasPointerCapture(event.pointerId)) {
      this.controls.movePad.releasePointerCapture(event.pointerId);
    }

    this.movePointerId = null;
    this.mobileMoveInput = { x: 0, y: 0, z: 0 };
    this.controls.moveThumb.style.transform = 'translate(0px, 0px)';
    this.updateMovement();
    event.preventDefault();
  }

  private updateMovePad(event: PointerEvent): void {
    if (!this.controls) return;

    const rect = this.controls.movePad.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const radius = Math.min(rect.width, rect.height) * 0.32;

    let dx = event.clientX - centerX;
    let dy = event.clientY - centerY;
    const distance = Math.hypot(dx, dy);

    if (distance > radius) {
      dx = (dx / distance) * radius;
      dy = (dy / distance) * radius;
    }

    this.controls.moveThumb.style.transform = `translate(${dx}px, ${dy}px)`;
    this.mobileMoveInput = {
      x: dx / radius,
      y: 0,
      z: dy / radius,
    };
    this.updateMovement();
  }

  private onLookPadPointerDown(event: PointerEvent): void {
    if (!this.isTouchPointer(event) || !this.controls || this.lookPointerId !== null) return;

    this.lookPointerId = event.pointerId;
    this.lastLookX = event.clientX;
    this.lookStartX = event.clientX;
    this.lookStartY = event.clientY;
    this.lookMoved = false;
    this.controls.lookPad.setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  private onLookPadPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.lookPointerId) return;

    const deltaX = event.clientX - this.lastLookX;
    this.lastLookX = event.clientX;
    if (Math.hypot(event.clientX - this.lookStartX, event.clientY - this.lookStartY) > TOUCH_TAP_SLOP) {
      this.lookMoved = true;
    }
    this.rotateCamera(deltaX * -LOOK_SENSITIVITY);
    event.preventDefault();
  }

  private onLookPadPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.lookPointerId || !this.controls) return;

    if (this.controls.lookPad.hasPointerCapture(event.pointerId)) {
      this.controls.lookPad.releasePointerCapture(event.pointerId);
    }

    this.lookPointerId = null;
    this.lookMoved = false;
    event.preventDefault();
  }

  private rotateCamera(deltaYaw: number): void {
    this.cameraYaw += deltaYaw;
    this.updateMovement();
  }

  private updateMovement(): void {
    const dir = { x: 0, y: 0, z: 0 };

    if (this.keys.has('w') || (this.isLeftMouseDown && this.isRightMouseDown)) dir.z -= 1;
    if (this.keys.has('s')) dir.z += 1;
    if (this.keys.has('a')) dir.x -= 1;
    if (this.keys.has('d')) dir.x += 1;

    dir.x += this.mobileMoveInput.x;
    dir.z += this.mobileMoveInput.z;

    const combinedLength = Math.hypot(dir.x, dir.z);
    if (combinedLength > 1) {
      dir.x /= combinedLength;
      dir.z /= combinedLength;
    }

    if (dir.x === 0 && dir.z === 0) {
      if (this.lastMovePayload !== 'stop') {
        this.net.send({ type: ClientMessageType.StopMove });
        this.lastMovePayload = 'stop';
        this.lastSentRotation = Number.NaN;
      }
      return;
    }

    const cos = Math.cos(this.cameraYaw);
    const sin = Math.sin(this.cameraYaw);
    const rotated: Vec3 = {
      x: dir.x * cos - dir.z * sin,
      y: 0,
      z: dir.x * sin + dir.z * cos,
    };

    const rotatedLength = Math.hypot(rotated.x, rotated.z);
    if (rotatedLength > 0) {
      rotated.x /= rotatedLength;
      rotated.z /= rotatedLength;
    }

    const movePayload = `${rotated.x.toFixed(3)},${rotated.z.toFixed(3)}`;
    if (movePayload !== this.lastMovePayload) {
      this.net.send({ type: ClientMessageType.Move, direction: rotated });
      this.lastMovePayload = movePayload;
    }

    const rotation = Math.atan2(-rotated.x, -rotated.z);
    if (Number.isNaN(this.lastSentRotation) || Math.abs(this.lastSentRotation - rotation) > 0.01) {
      this.net.send({ type: ClientMessageType.Rotate, rotation });
      this.lastSentRotation = rotation;
    }
  }

  private isTouchPointer(event: PointerEvent): boolean {
    return event.pointerType === 'touch' || event.pointerType === 'pen';
  }

  getCameraYaw(): number {
    return this.cameraYaw;
  }

  setCameraYaw(yaw: number): void {
    this.cameraYaw = yaw;
  }

  getMousePosition(): { x: number; y: number } {
    return { x: this.mouseX, y: this.mouseY };
  }

  isKeyDown(key: string): boolean {
    return this.keys.has(key);
  }
}
